import React from "react";

/**
 * lazyRoute — React.lazy with an imperative preload() and a hydration-safe
 * fast path. When preload() has resolved BEFORE the first render (index.js
 * awaits it for the current route when hydrating prerendered HTML), the
 * component renders synchronously and NO Suspense boundary crosses hydration.
 * On client-side navigation without preload, it behaves like React.lazy with
 * the given visible fallback.
 */
export function lazyRoute(loader, fallback) {
  let Comp = null;
  let promise = null;
  // Lazy start: the chunk is only fetched when the route is rendered
  // (client navigation) or explicitly preloaded before hydration (index.js).
  const load = () => (promise ||= loader().then((m) => {
    Comp = m.default || m;
    return m; // React.lazy needs the resolved MODULE, not undefined
  }));
  const LazyInner = React.lazy(load);

  function LazyRoute(props) {
    if (Comp) return <Comp {...props} />;
    return (
      <React.Suspense fallback={fallback}>
        <LazyInner {...props} />
      </React.Suspense>
    );
  }
  LazyRoute.load = load;
  LazyRoute.isLoaded = () => Comp !== null;
  return LazyRoute;
}

export const SRP_FALLBACK = (
  <div data-testid="srp-loading" className="min-h-screen bg-[#030712] px-6 pt-32 text-white">
    <div className="mx-auto max-w-4xl rounded-sm border border-[#3B82F6]/25 bg-[#07111F] p-6">
      CRA SRP Reporting Assistant wird geladen…
    </div>
  </div>
);

// Visible, non-black fallback for code-split content routes (client-side
// navigation only — prerendered pages hydrate without it).
export const ROUTE_FALLBACK = (
  <div className="min-h-screen bg-[#030712] px-6 pt-32 text-white">
    <div className="mx-auto max-w-4xl rounded-sm border border-[#3B82F6]/25 bg-[#07111F] p-6">
      <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#3B82F6]">NexTech.Law</span>
      <p className="mt-3 text-gray-300">Inhalt wird geladen…</p>
    </div>
  </div>
);
